import React, { useState, useEffect, useCallback } from 'react';
import { Modal, CircularProgress } from '@material-ui/core';
import { Button } from '../atoms/ButtonWithAuth';
import { IconButton } from '../atoms/IconButtonWithAuth';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import { CustomTextField } from '../atoms/TextFieldWithAuth';
import Redux from '../../redux/ReduxConnector';
import { useHistory } from 'react-router-dom';
import { ifBasePath } from '../../config/baseConfig';

import { BasicItemKeys } from '../../enums/item/basic-item-keys';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    container: {
      background: 'var(--color-white)',
      borderRadius: 10,
      width: 650,
      height: 'auto',
      margin: '4px auto 0',
      textAlign: 'left'
    },
    header: {
      borderRadius: '10px 10px 0 0',
      height: 64,
      backgroundColor: 'var(--color-admin-key)',
      position: 'relative',
      '& h2': {
        textAlign: 'left',
        color: 'var(--color-white)',
        fontSize: 20,
        marginLeft: 20,
        lineHeight: '64px',
        fontWeight: 500,
        letterSpacing: '0.5px'
      }
    },
    closeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      color: 'var(--color-white)'
    },
    formContainer: {
      padding: '10px 20px'
    },
    isChanged: {
      backgroundColor: '#DEF2FF'
    },
    lognTextField: {
      width: 610,
      margin: '6px 0 0',
      '& input': {
        fontSize: 14
      }
    },
    buttonsContainer: {
      marginTop: 16,
      borderTop: '1px solid #E0E0E0',
      textAlign: 'right',
      padding: 20
    },
    cancelButton: {
      padding: '8px 28px',
      borderRadius: 999,
      backgroundColor: 'var(--color-white)',
      color: 'var(--color-gray-3)',
      marginRight: 8,
      '&:hover': {
        backgroundColor: '#f7f7f7'
      }
    },
    submitButton: {
      padding: '8px 28px',
      borderRadius: 999,
      backgroundColor: 'var(--color-admin-key)',
      color: 'var(--color-white)',
      '&:disabled': {
        backgroundColor: '#e0e0e0'
      },
      '&:hover': {
        backgroundColor: '#1f6fda'
      }
    },
    progress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translateY(-50%) translateX(-50%)'
    }
  })
);

interface Props {
  isOpen: boolean;
  setIsOpen: (e: boolean) => void;
  contractId: any;
  note: string;
  detectError: () => void;
}

const OperationNoteEditModal: React.FC<Props> = props => {
  const { contractId, note } = props;

  const styles = useStyles({});
  const history = useHistory();
  const [progress, setProgress] = useState<boolean>(false);
  const [parameter, setParameter] = useState('');

  useEffect(() => {
    setParameter(note);
  }, [note]);

  const handleNote = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const noteText = e.target.value;
    setParameter(noteText);
  }, []);

  const resetState = useCallback(() => {
    setParameter(note);
  }, [note]);

  const handleBack = useCallback(() => {
    props.setIsOpen(false);
    resetState();
  }, [props, resetState]);

  const handleChange = useCallback(async () => {
    setProgress(true);
    const method = 'POST';
    const url = `${ifBasePath}application/${contractId}/note`;
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-api-key': Redux.getStore().user.accessToken
    };
    const body = JSON.stringify({
      [BasicItemKeys.Note]: parameter
    });
    console.log('body:', body);

    try {
      const res = await fetch(url, {
        method,
        headers,
        body,
        mode: 'cors'
      });
      if (!res.ok) throw new Error(`${res.status}`);
      history.go(0);
      props.setIsOpen(false);
    } catch (error) {
      console.error(error);
      props.detectError();
      props.setIsOpen(false);
    } finally {
      setProgress(false);
    }
  }, [contractId, history, note, parameter, props]);

  return (
    <Modal
      aria-labelledby='simple-modal-title'
      aria-describedby='simple-modal-description'
      open={props.isOpen}
      className={styles.modal}
      onClose={() => props.setIsOpen(false)}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h2>備考の編集</h2>
          <IconButton
            className={styles.closeButton}
            onClick={() => {
              props.setIsOpen(false);
              resetState();
            }}
          >
            <Close />
          </IconButton>
        </header>
        <div className={styles.formContainer}>
          <EditField
            placeholder={'備考'}
            defaultValue={parameter}
            isChanged={parameter !== note}
            onChange={handleNote}
          />
        </div>

        <div className={styles.buttonsContainer}>
          <Button className={styles.cancelButton} onClick={handleBack} disabled={progress}>
            キャンセル
          </Button>
          <Button className={styles.submitButton} onClick={handleChange} disabled={progress}>
            変更
          </Button>
        </div>
        {progress && (
          <div className={styles.progress}>
            <CircularProgress />
          </div>
        )}
      </div>
    </Modal>
  );
};

const EditField = (props: {
  placeholder: string;
  defaultValue?: string;
  onChange?: any;
  isChanged?: boolean;
  error?: boolean;
  helperText?: string;
}) => {
  const styles = useStyles({});
  let className = styles.lognTextField;
  className = !props.error && props.isChanged ? `${className} ${styles.isChanged}` : className;
  return (
    <CustomTextField
      className={className}
      placeholder={props.placeholder}
      multiline
      rows={7}
      defaultValue={props.defaultValue}
      helperText={props.helperText}
      margin={'dense'}
      InputLabelProps={{
        shrink: true
      }}
      error={props.error}
      variant='outlined'
      onChange={props.onChange}
    />
  );
};

export default OperationNoteEditModal;
